import { Helmet } from '@/components/Seo';
import Header from '@/components/habitus/Header';
import Hero from '@/components/habitus/Hero';
import Testimonials from '@/components/habitus/Testimonials';
import Structure from '@/components/habitus/Structure';
import Team from '@/components/habitus/Team';
import Differentials from '@/components/habitus/Differentials';
import Contact from '@/components/habitus/Contact';
import Footer from '@/components/habitus/Footer';
import WhatsAppButton from '@/components/habitus/WhatsAppButton';
import BlogHighlights from '@/components/habitus/BlogHighlights';
import { usePageView } from '@/hooks/useTrackEvent';
import { CLINIC_INFO } from '@/data/constants';

// No do site, para as paginas (blog, artigos) apontarem com `isPartOf` e o
// grafo ter uma raiz alem da clinica. Sem `SearchAction`: o site nao tem busca.
const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.clinicahabitus.com.br/#website",
  "url": "https://www.clinicahabitus.com.br",
  "name": CLINIC_INFO.name,
  "inLanguage": "pt-BR",
  "publisher": { "@id": "https://www.clinicahabitus.com.br/#organization" },
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  // MedicalClinic, nao MedicalBusiness: e o subtipo especifico, e o
  // TreatmentSchema ja declarava este mesmo @id como MedicalClinic. Dois
  // @type diferentes para o mesmo @id deixavam o grafo ambiguo.
  "@type": "MedicalClinic",
  "@id": "https://www.clinicahabitus.com.br/#organization",
  "name": CLINIC_INFO.name,
  // Mesma lista de /sobre-a-clinica: o @id e um so, entao o grafo nao pode
  // descrever a entidade com nomes diferentes em paginas diferentes.
  "alternateName": ["Habitus", "Clínica Habitus Saúde Integrativa"],
  "description": "Clínica de Saúde Integrativa em Brasília. Medicina personalizada, nutrição funcional, soroterapia e terapias complementares para tratamento de dor crônica, fibromialgia, fadiga e emagrecimento.",
  "url": "https://www.clinicahabitus.com.br",
  "telephone": CLINIC_INFO.callPhone,
  "email": CLINIC_INFO.email,
  "foundingDate": CLINIC_INFO.foundingYear.toString(),
  "identifier": {
    "@type": "PropertyValue",
    "propertyID": "Registro CRM-DF",
    "value": CLINIC_INFO.registroCrm,
  },
  // Sem `priceRange` por enquanto. O valor era "$$", mas o vite-react-ssg injeta
  // o HTML com String.replace, que trata "$$" como escape de "$": o HTML
  // publicado saia com "$" (faixa mais barata). Qualquer "$$" em texto
  // pre-renderizado sofre o mesmo. Se a clinica quiser expor uma faixa, usar
  // texto com um cifrao por vez, ex.: "R$ 300 - R$ 900".
  "image": "https://www.clinicahabitus.com.br/og-image.jpg",
  "logo": "https://www.clinicahabitus.com.br/logo-habitus.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": `${CLINIC_INFO.address.street}, ${CLINIC_INFO.address.complement}`,
    "addressLocality": "Brasília",
    "addressRegion": "DF",
    "postalCode": CLINIC_INFO.address.zipCode,
    "addressCountry": "BR"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": CLINIC_INFO.geo.latitude,
    "longitude": CLINIC_INFO.geo.longitude
  },
  "hasMap": CLINIC_INFO.googleMaps,
  // Sem `aggregateRating`: nota que o proprio negocio coleta e exibe sobre si
  // mesmo e auto-servida -- nao rende rich result em LocalBusiness e conta como
  // spam de dados estruturados. Ver a nota em habitus/Testimonials.tsx.
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00"
    }
  ],
  // O Perfil da Empresa entra aqui junto das redes: e o perfil de terceiro que
  // mais ancora a identidade do negocio para o Google.
  "sameAs": [
    CLINIC_INFO.socialLinks.instagram,
    CLINIC_INFO.socialLinks.facebook,
    CLINIC_INFO.socialLinks.youtube,
    CLINIC_INFO.socialLinks.doctoralia,
    CLINIC_INFO.googleMaps
  ],
  // Regiao atendida em texto: e o que responde "atende Aguas Claras?" para o
  // Google e para as IAs. Sem `medicalSpecialty` aqui e sem `serviceArea`
  // inventado: sao os bairros de onde a clinica de fato recebe pacientes.
  "areaServed": [
    { "@type": "City", "name": "Brasília", "sameAs": "https://pt.wikipedia.org/wiki/Bras%C3%ADlia" },
    { "@type": "Place", "name": "Sudoeste e Octogonal" },
    { "@type": "Place", "name": "Asa Sul" },
    { "@type": "Place", "name": "Asa Norte" },
    { "@type": "Place", "name": "Águas Claras" },
    { "@type": "Place", "name": "Lago Sul" },
    { "@type": "Place", "name": "Guará" }
  ],
  // Temas que a clinica atende, como condicoes e procedimentos (com MeSH onde
  // existe). NAO e especialidade: `medicalSpecialty` exige RQE (ver
  // TreatmentSchema.tsx), `knowsAbout` e so assunto.
  "knowsAbout": [
    { "@type": "MedicalCondition", "name": "Fibromialgia", "sameAs": "https://id.nlm.nih.gov/mesh/D005356" },
    { "@type": "MedicalCondition", "name": "Resistência à insulina", "sameAs": "https://id.nlm.nih.gov/mesh/D007333" },
    { "@type": "MedicalCondition", "name": "Menopausa e climatério", "sameAs": "https://id.nlm.nih.gov/mesh/D008593" },
    { "@type": "MedicalProcedure", "name": "Acompanhamento pós-cirurgia bariátrica", "sameAs": "https://id.nlm.nih.gov/mesh/D050110" },
    { "@type": "MedicalCondition", "name": "Fadiga crônica" },
    { "@type": "MedicalCondition", "name": "Deficiências de vitaminas e minerais" },
    { "@type": "MedicalProcedure", "name": "Soroterapia (reposição endovenosa de nutrientes)" },
    { "@type": "MedicalProcedure", "name": "Ozonioterapia nas indicações da Resolução CFM 2.445/2025" },
    { "@type": "MedicalTest", "name": "Bioimpedância octapolar InBody" }
  ],
  // Sem `medicalSpecialty`: ver a nota em treatments/TreatmentSchema.tsx.
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Serviços de Saúde Integrativa",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Soroterapia",
          "description": "Infusão de vitaminas e minerais diretamente na corrente sanguínea"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Tratamento de Fibromialgia",
          "description": "Abordagem integrativa para controle da dor crônica e fibromialgia"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Acompanhamento Pós-Bariátrica",
          "description": "Suporte nutricional e clínico para pacientes de cirurgia bariátrica"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalProcedure",
          "name": "Programa de Emagrecimento",
          "description": "Plano Emagreça+ com abordagem personalizada para perda de peso saudável"
        }
      }
    ]
  }
};

const Index = () => {
  usePageView('Home');

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Clínica Habitus | Saúde Integrativa em Brasília</title>
        <meta name="description" content="Clínica Habitus - Saúde Integrativa em Brasília. Medicina personalizada, nutrição especializada, soroterapia e terapias complementares. Agende sua consulta." />
        <link rel="canonical" href="https://www.clinicahabitus.com.br/" />
        <meta property="og:title" content="Clínica Habitus | Saúde Integrativa em Brasília" />
        <meta property="og:description" content="Medicina personalizada, nutrição especializada, soroterapia e terapias complementares. Agende sua consulta." />
        <meta property="og:url" content="https://www.clinicahabitus.com.br/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.clinicahabitus.com.br/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clínica Habitus | Saúde Integrativa em Brasília" />
        <meta name="twitter:description" content="Medicina personalizada, nutrição especializada, soroterapia e terapias complementares." />
        <meta name="twitter:image" content="https://www.clinicahabitus.com.br/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webSiteSchema)}
        </script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <Testimonials />
        <Structure />
        <Team />
        <Differentials />
        <Contact />
        <BlogHighlights />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Index;
